// Landing page for the sign-up confirmation email link.
//
// Flow:
//   1. User taps "Confirm your email" in the verification email.
//   2. Supabase validates the token server-side, marks the user confirmed,
//      and 302s the browser to this page. In the implicit flow it appends
//      a hash like `#access_token=...&type=signup`; in the PKCE flow a
//      `?code=...` query param.
//   3. The Supabase client was initialised with `detectSessionInUrl: true`
//      in src/lib/supabase.js, so it consumes that hash/query automatically
//      on module load. By the time React renders us, either there is a
//      session OR the URL contains an error param.
//   4. We read the session once and show either a success state ("open
//      the app and sign in") or an error state (expired link, already
//      used — plus a resend option).
//
// Note: the native iOS build never lands here — email links always open
// in the user's default browser (Safari). This page is the web handoff;
// success just tells the user to go back to the installed app. If we
// later add Universal Links, the email link can open the app directly.
//
// Design: mirrors ResetPassword.jsx styling so the two landing pages
// look like part of the same brand.

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { useAuth } from './useAuth.js';

// Read both the hash (#a=b&c=d) and the query string (?a=b&c=d) into one
// param bag. Supabase can use either depending on the flow type.
function readAuthParams() {
  const out = {};
  const hash = typeof location !== 'undefined' ? location.hash.replace(/^#/, '') : '';
  const search = typeof location !== 'undefined' ? location.search.replace(/^\?/, '') : '';
  for (const raw of [hash, search]) {
    if (!raw) continue;
    for (const kv of raw.split('&')) {
      if (!kv) continue;
      const [k, v] = kv.split('=');
      if (k) out[decodeURIComponent(k)] = v ? decodeURIComponent(v) : '';
    }
  }
  return out;
}

export function ConfirmEmail() {
  const { resendConfirmation } = useAuth();
  // 'checking' | 'ok' | 'error'
  const [status, setStatus] = useState('checking');
  const [errorMsg, setErrorMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | failed
  const [resendError, setResendError] = useState('');

  const urlParams = useMemo(() => readAuthParams(), []);

  useEffect(() => {
    // Supabase consumes the URL hash synchronously when the client module
    // loads, but the session write is async. Poll briefly for a session
    // before giving up. ~1s total is plenty.
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        if (!cancelled) setStatus('ok');
        return;
      }
      // If Supabase sent back an explicit error, surface it now.
      const err = urlParams.error_description || urlParams.error;
      if (err) {
        if (!cancelled) {
          setErrorMsg(decodeError(err));
          setStatus('error');
        }
        return;
      }
      // No session and no explicit error yet — retry for ~1s.
      if (tries++ < 10) {
        setTimeout(tick, 100);
      } else {
        // Fallback: no session detected and no error param — treat as an
        // already-used or stale link.
        if (!cancelled) {
          setErrorMsg("This confirmation link is no longer valid. It may have already been used or expired.");
          setStatus('error');
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [urlParams]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendState('sending');
    setResendError('');
    try {
      await resendConfirmation(resendEmail.trim());
      setResendState('sent');
    } catch (err) {
      setResendState('failed');
      setResendError(err?.message || String(err));
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        {status === 'checking' && (
          <>
            <h1 className="auth-title">Confirming your email…</h1>
            <p className="auth-sub">This should only take a moment.</p>
          </>
        )}

        {status === 'ok' && (
          <>
            <h1 className="auth-title">You're all set</h1>
            <p className="auth-sub">
              Your email is confirmed. Open Consistency on your phone and
              sign in to start building your routine.
            </p>
            <div className="auth-info" style={{ marginTop: 14 }}>
              Already on your phone? <a href="/" style={{ color: 'var(--accent-hi)' }}>Open the app →</a>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="auth-title">We couldn't confirm this link</h1>
            <p className="auth-sub">{errorMsg}</p>

            {resendState === 'sent' ? (
              <div className="auth-info" style={{ marginTop: 14 }}>
                New confirmation email sent. Check your inbox.
              </div>
            ) : (
              <form onSubmit={handleResend} style={{ marginTop: 12 }}>
                <div className="auth-field">
                  <input
                    className="input"
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email to resend"
                    value={resendEmail}
                    onChange={e => setResendEmail(e.target.value)}
                    required
                  />
                </div>
                {resendError && <div className="auth-error">{resendError}</div>}
                <button
                  className="auth-submit"
                  type="submit"
                  disabled={resendState === 'sending'}
                >
                  {resendState === 'sending' ? 'Sending…' : 'Send a new confirmation email'}
                </button>
              </form>
            )}

            <div className="auth-switch" style={{ marginTop: 18 }}>
              <a href="/" className="auth-link" style={{ color: 'var(--accent-hi)' }}>
                Back to Consistency →
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Supabase error strings sometimes arrive URL-encoded with +'s for spaces.
function decodeError(raw) {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}
